import { View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import {
  PMBarChartCard,
  PMDonutChartCard,
} from "@pimo/pimo-components/src/lib/pm-components";
import { ReportingItem } from "vmd-types";

import { TestAppState } from "../../../app";
import { DONUT_CARD_LAYOUT, DONUT_CHART_COLORS } from "../../../constants";
import {
  DashboardChartEntry,
  getAllReportingItems,
  withTotalInTitle,
} from "../../../helpers/pages-helpers/dashboard-plugin-helpers";

const getDistributionChannelData = (
  items: ReportingItem[],
  distributionChannelDefinition?: { id: string; label: string }[]
): { entries: DashboardChartEntry[] } => {
  const channelCounts = new Map<string, { label: string; count: number }>();

  for (const item of items) {
    const channelIds = item.distributionChannel ?? [];
    if (!channelIds.length) {
      continue;
    }

    for (const channelId of channelIds) {
      const existing = channelCounts.get(channelId) || {
        label:
          distributionChannelDefinition?.find((c) => c.id === channelId)
            ?.label || channelId,
        count: 0,
      };

      channelCounts.set(channelId, {
        ...existing,
        count: existing.count + 1,
      });
    }
  }

  const entries = Array.from(channelCounts.values())
    .sort((a, b) => b.count - a.count)
    .map((entry, index) => ({
      label: entry.label,
      count: entry.count,
      color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
    }));

  return { entries };
};

/**
 * The bar and donut chart pair showing systems per distribution channel
 * (#2222) — hand-duplicated copies of the same data before this split.
 */
export function addDistributionChannelChartsCard(
  view: View<TestAppState, DragAndDropLayout>
): void {
  const distributionChannelsBarChartComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "distributionChannelsDashboardBarChartComponent",
      labels: ["barChart", "chart", "distributionChannel"],
      description:
        "Shows systems grouped by distribution channel across all projects.",
      title: "Systems per Distribution Channels",
    },
  });

  distributionChannelsBarChartComponent.mapState(({ dashboard, settings }) => {
    const allItems = getAllReportingItems(dashboard);
    const { entries } = getDistributionChannelData(
      allItems,
      settings?.configuration?.distributionChannelDefinition
    );

    return {
      title: "Systems per Distribution Channels",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Systems",
      yLabel: "Distribution Channel",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  const distributionChannelsDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "distributionChannelsDashboardDonutChartComponent",
      labels: ["donutChart", "chart", "distributionChannel"],
      description:
        "Shows systems grouped by distribution channel across all projects.",
      title: "Systems per Distribution Channels ",
    },
  });

  distributionChannelsDonutChartComponent.mapState(
    ({ dashboard, settings }) => {
      const allItems = getAllReportingItems(dashboard);
      const { entries } = getDistributionChannelData(
        allItems,
        settings?.configuration?.distributionChannelDefinition
      );

      return {
        title: withTotalInTitle(
          "Systems per Distribution Channels",
          allItems.length
        ),
        icon: "DonutLarge",
        ...DONUT_CARD_LAYOUT,
        data: entries.map((entry) => ({
          label: entry.label,
          value: entry.count,
          color: entry.color,
        })),
      };
    }
  );
}
